import React from 'react';
import { ApolloError, gql } from '@apollo/client';
import {
  SocialOAuthContext,
  SocialOAuthProvider,
  useJoinOrganizationFromInvitationMutation,
  useLoginMutation,
  useLoginWithSocialMutation,
  useStartSocialOAuthMutation
} from '../../../models/graphql';
import { LoginController, LoginControllerProps } from './Login.controller';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';

gql`
  mutation Login($input: LoginWithEmailInput!) {
    loginWithEmail(input: $input) {
      user {
        id
        organizations {
          id
          workspaces {
            id
          }
        }
      }
      token {
        ...CoreTokenFields
      }
    }
  }

  mutation LoginWithSocial($socialAuthToken: String!) {
    loginWithSocial(socialAuthToken: $socialAuthToken) {
      user {
        id
        organizations {
          id
          workspaces {
            id
          }
        }
      }
      token {
        ...CoreTokenFields
      }
    }
  }
`;

export type LoginApolloProps = Omit<
  LoginControllerProps,
  | 'joinCalled'
  | 'joinOrganizationData'
  | 'joinOrganizationFromInvitation'
  | 'login'
  | 'loginError'
  | 'loginWithSocial'
  | 'socialData'
  | 'socialLoginData'
  | 'startSocialOAuth'
  | 'submitted'
> & {
  // add types
};

export const LoginApollo = (props: LoginApolloProps): JSX.Element => {
  const [login, { error: loginApolloError, called: loginApolloCalled }] = useLoginMutation({
    fetchPolicy: 'no-cache'
  });
  const [startSocialOAuth, { data: socialData, error: socialApolloError, called: socialCalled }] =
    useStartSocialOAuthMutation({
      variables: {
        oAuthProvider: SocialOAuthProvider.FACEBOOK,
        context: SocialOAuthContext.LOGIN
      },
      fetchPolicy: 'no-cache'
    });
  const [loginWithSocial, { error: socialLoginError, called: socialLoginCalled }] = useLoginWithSocialMutation({
    fetchPolicy: 'no-cache'
  });
  const [joinOrganizationFromInvitation, { error: joinError, called: joinCalled }] =
    useJoinOrganizationFromInvitationMutation({ fetchPolicy: 'no-cache' });
  let loginError: string | JSX.Element = '';
  let submitted = false;

  if (
    (joinCalled && !joinError) ||
    (socialCalled && !socialApolloError) ||
    (socialLoginCalled && !socialLoginError) ||
    (loginApolloCalled && !loginApolloError)
  ) {
    submitted = true;
    loginError = '';
  }
  if (loginApolloError || socialApolloError || socialLoginError || joinError) {
    const errorForRemapping = (loginApolloError || socialApolloError || socialLoginError || joinError) as ApolloError;

    loginError = remapApolloErrors(errorForRemapping);
    submitted = false;
  }

  const controllerProps: LoginControllerProps = {
    ...props,
    joinOrganizationFromInvitation,
    login,
    loginError,
    loginWithSocial,
    socialData: socialData?.startSocialOAuth,
    startSocialOAuth,
    submitted
  };

  return <LoginController {...controllerProps} />;
};
